import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import HelpHint from './HelpHint';
import { Separator } from './ui/separator';
import {
  Plus,
  Edit,
  Trash2,
  Globe,
  Copy,
  Search as SearchIcon,
  Target,
  Zap,
  Archive,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';

export default function TrafficSources() {
  const { user } = useAuth();
  const { t } = useT();
  const [sources, setSources] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateCategories, setTemplateCategories] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_name: '',
    postback_url: '',
    accept_parameters: [],
    parameters: {},
  });

  useEffect(() => {
    Promise.all([fetchTrafficSources(), fetchTemplates()]);
  }, []);

  const fetchTrafficSources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/traffic-sources`);
      setSources(response.data);
    } catch (error) {
      console.error('Error fetching traffic sources:', error);
      toast.error(t('Error loading traffic sources'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      // Curated registry + the imported catalog (~175 sources).
      const response = await axios.get(`${API}/traffic-sources/templates`);
      const items = (response.data.templates || []).map((tpl) => ({
        template_name: tpl.slug,
        name: tpl.name,
        category: tpl.category || '',
        aliases: tpl.aliases || [],
        parameters: tpl.parameters || {},
        postback_url: (tpl.outbound_postback && tpl.outbound_postback.url) || '',
      }));
      setTemplates(items);
      setTemplateCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreateSource = async () => {
    try {
      const response = await axios.post(`${API}/traffic-sources`, formData);
      setSources([...sources, response.data]);
      setCreateDialogOpen(false);
      resetForm();
      toast.success(t('Traffic source created'));
    } catch (error) {
      console.error('Error creating source:', error);
      toast.error(t('Error creating traffic source'));
    }
  };

  const handleUpdateSource = async () => {
    if (!editingSource) return;
    try {
      const response = await axios.put(`${API}/traffic-sources/${editingSource.id}`, formData);
      setSources(sources.map((s) => (s.id === editingSource.id ? response.data : s)));
      setEditDialogOpen(false);
      setEditingSource(null);
      resetForm();
      toast.success(t('Traffic source updated'));
    } catch (error) {
      console.error('Error updating source:', error);
      toast.error(t('Error updating traffic source'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteSource = async (sourceId) => {
    try {
      await axios.delete(`${API}/traffic-sources/${sourceId}`);
      setSources(sources.filter((s) => s.id !== sourceId));
      setDeleteTarget(null);
      toast.success(t('Traffic source archived'));
    } catch (error) {
      console.error('Error deleting source:', error);
      if (error.response?.status === 400) {
        toast.error(t('Cannot delete: source is in use by campaigns'));
      } else {
        toast.error(t('Error deleting traffic source'));
      }
    }
  };

  const handleTemplateSelect = (templateName) => {
    const template = templates.find((tpl) => tpl.template_name === templateName);
    if (template) {
      setFormData((prev) => ({
        ...prev,
        template_name: templateName,
        name: template.name,
        parameters: template.parameters,
        postback_url: template.postback_url || '',
      }));
    }
  };

  const visibleTemplates = templates.filter((tpl) => {
    if (templateCategory !== 'all' && tpl.category !== templateCategory) return false;
    const q = templateSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      tpl.name.toLowerCase().includes(q) ||
      tpl.template_name.includes(q) ||
      tpl.aliases.some((a) => a.toLowerCase().includes(q))
    );
  });

  const [newParamName, setNewParamName] = useState('');

  const addParameter = () => {
    const name = newParamName.trim();
    if (!name) return;
    setFormData((prev) => ({
      ...prev,
      parameters: { ...prev.parameters, [name]: { alias: '', placeholder: '' } },
    }));
    setNewParamName('');
  };

  const removeParameter = (paramName) => {
    setFormData((prev) => {
      const next = { ...prev.parameters };
      delete next[paramName];
      return { ...prev, parameters: next };
    });
  };

  const updateParameter = (paramName, field, value) => {
    setFormData((prev) => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [paramName]: { ...prev.parameters[paramName], [field]: value },
      },
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      template_name: '',
      postback_url: '',
      accept_parameters: [],
      parameters: {},
    });
  };

  const openEditSource = (source) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      description: source.description || '',
      template_name: source.template_name || '',
      postback_url: source.postback_url || '',
      accept_parameters: source.accept_parameters || [],
      parameters: source.parameters || {},
    });
    setEditDialogOpen(true);
  };

  const copyPostbackUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success(t('Postback URL copied'));
  };

  const getSourceIcon = () => <Globe className="w-5 h-5 text-primary" />;

  const filteredSources = sources.filter(
    (source) =>
      source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (source.description && source.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const ParametersBlock = () => (
    <div className="space-y-4">
      <Label className="text-base font-semibold">{t('UTM parameters')} <HelpHint text={t("Parameters the platform passes into the click (sub1…subN, gclid, tm, etc.). They become macros, appear in reports and are added to the tracking link.")} /></Label>
      <div className="flex gap-2">
        <Input
          value={newParamName}
          onChange={(e) => setNewParamName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addParameter())}
          placeholder={t('Parameter name, e.g. sub1')}
          className="flex-1"
        />
        <Button variant="outline" size="sm" onClick={addParameter} disabled={!newParamName.trim()}>
          <Plus className="w-4 h-4 mr-1" />
          {t('Add')}
        </Button>
      </div>

      {Object.keys(formData.parameters).length > 0 ? (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2 text-sm font-medium text-muted-foreground">
            <span>{t('Parameter')}</span>
            <span>{t('Alias')}</span>
            <span>{t('Placeholder')}</span>
            <span>{t('Action')}</span>
          </div>
          {Object.entries(formData.parameters).map(([paramName, paramData]) => (
            <div key={paramName} className="grid grid-cols-4 gap-2 items-center">
              <Input value={paramName} readOnly className="bg-surface-2" />
              <Input
                value={paramData.alias || ''}
                onChange={(e) => updateParameter(paramName, 'alias', e.target.value)}
                placeholder={t('Alias')}
              />
              <Input
                value={paramData.placeholder || ''}
                onChange={(e) => updateParameter(paramName, 'placeholder', e.target.value)}
                placeholder={t('Source macro')}
              />
              <Button variant="ghost" size="sm" aria-label={t('Remove parameter')} title={t('Remove parameter')} onClick={() => removeParameter(paramName)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-center py-4">{t('No parameters added')}</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Sources')}
        title={t('Traffic sources')}
        subtitle={t('Configure UTM templates and conversion postbacks for every ad network you run.')}
        actions={
          <>
          <Button asChild variant="outline" data-testid="traffic-sources-view-archived">
            <Link to="/archive">
              <Archive className="w-4 h-4 mr-2" />
              {t('View archived')}
            </Link>
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              {t('Create source')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('Create traffic source')}</DialogTitle>
              <DialogDescription>
                {t('Configure a traffic source for UTM tags and conversion postbacks')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label>{t('Quick start from template')} <HelpHint text={t("A ready platform preset (Google Ads, Facebook…) — fills in the name, the standard macros/subs and the S2S postback for this source.")} /></Label>
                <div className="flex gap-2">
                  <Input
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder={t('Search templates…')}
                    className="flex-1"
                    data-testid="template-search"
                  />
                  <Select value={templateCategory} onValueChange={setTemplateCategory}>
                    <SelectTrigger className="w-44" data-testid="template-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('All categories')}</SelectItem>
                      {templateCategories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {visibleTemplates.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
                    {visibleTemplates.map((tpl) => (
                      <button
                        key={tpl.template_name}
                        type="button"
                        title={tpl.category}
                        onClick={() => handleTemplateSelect(tpl.template_name)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                          formData.template_name === tpl.template_name
                            ? 'border-primary bg-primary/10 text-primary font-medium'
                            : 'border-border hover:border-primary/40 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {getSourceIcon()}
                        <span className="truncate">{tpl.name}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-3">
                    {t('No templates match these filters.')}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('Templates in catalog')}: {templates.length}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('Name')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={t('Traffic source name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('Postback URL')} <HelpHint text={t("Optional. An outbound postback TO the ad network on conversion — so the platform can optimize campaigns on its side (e.g. legacy Facebook CAPI).")} /></Label>
                  <Input
                    value={formData.postback_url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, postback_url: e.target.value }))}
                    placeholder="https://example.com/postback?..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('Description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder={t('Traffic source description')}
                />
              </div>

              <Separator />
              {ParametersBlock()}

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  {t('Cancel')}
                </Button>
                <Button onClick={handleCreateSource} disabled={!formData.name.trim()}>
                  {t('Create')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
          </>
        }
      />

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('Search traffic sources…')}
            className="pl-10"
          />
        </div>
      </div>

      {/* Sources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSources.length === 0 ? (
          <Card className="col-span-full">
            <CardContent style={{ padding: 0 }}>
              <XpnsrEmpty
                icon={Globe}
                title={searchTerm ? t('Sources not found') : t('No traffic sources')}
                subtitle={t('Create sources to integrate with ad networks')}
                cta={!searchTerm ? (
                  <button
                    type="button"
                    className="xp-btn xp-btn-primary"
                    onClick={() => setCreateDialogOpen(true)}
                    data-testid="empty-create-traffic-source"
                  >
                    <Plus style={{ width: 12, height: 12 }} /> {t('Create traffic source')}
                  </button>
                ) : null}
              />
            </CardContent>
          </Card>
        ) : (
          filteredSources.map((source) => (
            <Card key={source.id} className="hover:border-border-2 transition-colors">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {getSourceIcon()}
                    <CardTitle className="text-lg">{source.name}</CardTitle>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={source.is_active ? 'default' : 'secondary'}>
                      {source.is_active ? t('Active') : t('Inactive')}
                    </Badge>
                    <Button variant="ghost" size="sm" aria-label={t('Edit traffic source')} title={t('Edit traffic source')} onClick={() => openEditSource(source)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" aria-label={t('Delete traffic source')} title={t('Delete traffic source')} onClick={() => setDeleteTarget(source)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {source.description && <CardDescription>{source.description}</CardDescription>}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-lg font-semibold">{source.total_clicks || 0}</div>
                      <div className="text-xs text-muted-foreground">{t('Clicks')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{source.total_conversions || 0}</div>
                      <div className="text-xs text-muted-foreground">{t('Conversions')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">${source.total_cost?.toFixed(2) || '0.00'}</div>
                      <div className="text-xs text-muted-foreground">{t('Spend')}</div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-border">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('Parameters configured:')}</span>
                      <span className="font-medium">{Object.keys(source.parameters || {}).length}</span>
                    </div>
                  </div>

                  {source.postback_url && (
                    <div className="pt-3 border-t border-border">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">{t('Postback URL')}</Label>
                        <Button variant="ghost" size="sm" aria-label={t('Copy postback URL')} title={t('Copy postback URL')} onClick={() => copyPostbackUrl(source.postback_url)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-xs font-mono bg-surface-2 border border-border p-2 rounded mt-1 truncate">
                        {source.postback_url}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingSource(null); resetForm(); } }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Edit traffic source')}</DialogTitle>
            <DialogDescription>{t('Update traffic source settings')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('Name')}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={t('Traffic source name')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('Postback URL')} <HelpHint text={t("Optional. An outbound postback TO the ad network on conversion — so the platform can optimize campaigns on its side (e.g. legacy Facebook CAPI).")} /></Label>
                <Input
                  value={formData.postback_url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, postback_url: e.target.value }))}
                  placeholder="https://example.com/postback?..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('Description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t('Traffic source description')}
              />
            </div>

            <Separator />
            {ParametersBlock()}

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button onClick={handleUpdateSource} disabled={!formData.name.trim()}>
                {t('Save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Archive traffic source')}
        description={t('This will move "{name}" to the archive. You can restore it or delete it permanently from the Archive page.').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Archive')}
        variant="default"
        onConfirm={() => handleDeleteSource(deleteTarget?.id)}
      />
    </div>
  );
}
